import { Box, useMediaQuery } from "@mui/material";
import { color } from "chart.js/helpers";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useRef, useEffect } from "react";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { useTheme } from "@emotion/react";
import ZoomControls from "./ZoomControls";
import { formatNumber, formatNumberWithLocale, formatAxisValue } from "../../utils/charts";
//import regression from "regression";

ChartJS.register(
    CategoryScale,
    LinearScale,
    // Chart.js only ships the scales you register: without this the log-scale toggle throws
    // "logarithmic is not a registered scale" the moment it is switched on.
    LogarithmicScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    zoomPlugin
);


export default function LineChartJS({ data, showDataLabels, hideSubregions, title, regions, chartStacked, useLogScale, dualAxis }) {
    const stackedArea = chartStacked && chartStacked === 'area';

    const regionsToKeep = regions.filter(
        (region) => region.subtype === "toKeepOnChart"
    );

    const datasets = data?.datasets ?? [];
    const labels = data?.labels ?? [];

    // Sparse indicators (the forest ones report every five years) get an axis tick for every
    // year in the selected range, leaving two data points stranded among eight empty labels.
    // Only the years actually reported are labelled; the empty ones keep their slot, so the
    // spacing between readings stays honest. This is independent of whether the indicator
    // shows a reporting period - a value can be a point-in-time reading and still be sparse.
    const reportedYears = new Set(
        datasets.flatMap((dataset) => (dataset?.data ?? []).map((point) => String(point?.x)))
    );

    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);
    function hexToRGBA(hex, alpha = 1) {
        const [r, g, b] = hex
            .replace(/^#/, "")
            .match(/.{2}/g)
            .map((x) => parseInt(x, 16));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const fallbackColor = "rgba(0,0,0,1)";

    // Determine if there are multiple combinations of item/element/indicator codes
    const hasMultipleCombos = data.datasets.length > 1;
    // console.log('hasMultipleCombos', hasMultipleCombos, data?.info?.item, data);

    // Two quantities of very different size on one axis flatten the smaller one against the
    // baseline - milk against eggs, where milk is thirty times larger. With dual_axis the
    // smaller series moves to its own axis on the right. Only for exactly two series: with
    // more, which one belongs where stops being obvious.
    const seriesMaximum = (dataset) =>
        Math.max(...(dataset.data ?? [])
            .map((point) => (point && typeof point === "object" ? point.y : point))
            .map(Number)
            .filter(Number.isFinite), 0);
    const useDualAxis = Boolean(dualAxis) && datasets.length === 2 && !useLogScale;
    // The series name alone leaves the reader guessing what the numbers are; the unit alone
    // is ambiguous with two axes in the same unit. Both, when the unit is known.
    const axisTitle = (dataset) =>
        [dataset?.label, data.info.unit ? `(${data.info.unit})` : null].filter(Boolean).join(" ");
    const rightAxisIndex = useDualAxis
        ? (seriesMaximum(datasets[0]) < seriesMaximum(datasets[1]) ? 0 : 1)
        : -1;

    const chartData = {
        labels: labels,
        datasets: [
            ...datasets.map((dataset, index) => ({
                ...dataset,
                ...(index === rightAxisIndex ? { yAxisID: "y2" } : {}),
                fill: stackedArea ? true : false,
                backgroundColor: stackedArea
                    ? hexToRGBA(dataset.borderColor || fallbackColor, 0.65)
                    : dataset.borderColor || fallbackColor,
                hidden: hideSubregions
                    ? index !== 0 &&
                    ! (regionsToKeep.some((region) => region.label === dataset.label) || hasMultipleCombos)
                    : false,
            })),
        ].filter(Boolean),
    };

    const options = {
        layout: {
            padding: {
                top: isMobile ? 15 : 18,
                right: isMobile ? 20 : 25,
                bottom: isMobile ? 10 : 12,
                left: isMobile ? 5 : 12,
            },

        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: stackedArea ? true : false,
                grid: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    // Plain years on the axis - the reporting period belongs in the tooltip,
                    // where there is room for it. Returning undefined drops the tick, which is
                    // how the years with no reading are left unlabelled.
                    callback: function (value) {
                        const rawLabel = this.getLabelForValue(value);
                        return reportedYears.has(rawLabel) ? rawLabel : undefined;
                    },
                },
            },
            ...(useDualAxis ? {
                y2: {
                    position: "right",
                    beginAtZero: true,
                    // The gridlines of the left axis are the ones on screen: a second set
                    // would land between them and make the plot unreadable.
                    grid: { drawOnChartArea: false },
                    title: {
                        display: true,
                        text: axisTitle(datasets[rightAxisIndex]),
                        color: theme.palette.primary.fontColor,
                        padding: 0,
                    },
                    ticks: {
                        color: theme.palette.primary.fontColor,
                        padding: 10,
                        // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                        // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                        includeBounds: false,
                        callback: function (value, index, ticks) {
                            return formatAxisValue(value, ticks);
                        },
                    },
                },
            } : {}),
            y: {
                // A log axis cannot start at zero, and the tick step it produces is
                // multiplicative rather than additive, so the decimals heuristic below does
                // not apply either.
                ...(useLogScale ? { type: "logarithmic" } : { beginAtZero: true }),
                stacked: stackedArea ? true : false,
                grid: {
                    color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                },
                title: {
                    display: true,
                    text: useDualAxis
                        ? axisTitle(datasets.find((_, index) => index !== rightAxisIndex))
                        : data.info.unit,
                    color: theme.palette.primary.fontColor,
                    padding: 0,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    padding: 10,
                    // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                    // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                    includeBounds: false,
                    callback: function (value, index, ticks) {
                        return useLogScale ? formatNumberWithLocale(value) : formatAxisValue(value, ticks);
                    },
                },
            },
        },
        animation: {
            duration: 0,
        },
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    padding: isMobile ? 10 : 15,
                    usePointStyle: true,
                    color: theme.palette.primary.fontColor,
                    generateLabels: function (chart) {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const maxLength = 95; // max length of the legend text

                        return labelsOriginal
                            .map((label) => {
                                if (label.hidden && hideSubregions) {
                                    return null;
                                } else if (label.hidden && !hideSubregions) {
                                    label.fontColor =
                                        theme.palette.mode === "light"
                                            ? color(label.fontColor).lighten(2).rgbString()
                                            : "#666";
                                    label.fillStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.fillStyle).lighten(0.5).rgbString()
                                            : "#666";
                                    label.strokeStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.strokeStyle).lighten(0.5).rgbString()
                                            : "#666";
                                } else {
                                    label.fontColor = theme.palette.primary.fontColor;
                                }

                                // ensure that all items are shown in the legend
                                label.hidden = false;

                                // SAFETY: Only truncate if label.text is a string
                                if (typeof label.text === "string" && label.text.length > maxLength) {
                                    label.text = label.text.substring(0, maxLength) + "...";
                                }
                                return label;
                            })
                            .filter((label) => label !== null);
                    },
                },
            },
            datalabels: {
                formatter: function (value, context) {
                    const yValue = formatNumber(value.y);

                    //return `${y}\n${flagDescription}`;
                    return `${yValue}`;
                },
                color: theme.palette.primary.fontColor,
                align: "end",
                anchor: "end",
                clamp: true,
                display: showDataLabels ? "auto" : false, //toggle visibility instead of plugin
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: {
                    size: 10,
                },
                padding: 4,
            },
            tooltip: {
                callbacks: {
                    title: function (context) {
                        const rawLabel = context[0]?.label;
                        return data?.periodByYear?.[rawLabel] || rawLabel;
                    },
                    label: function (context) {
                        const label = context.dataset.label || "";
                        const value = formatNumberWithLocale(context.parsed?.y);
                        const unit = data.info.unit || "";

                        return `${label} ${value} ${unit}`;
                    },
                    labelTextColor: function (context) {
                        return "#000";
                    },
                    footer: function (context) {
                        const point = context[0]?.raw;
                        const notes = [];

                        // An annual figure built from monthly readings: say so when the year is
                        // not complete, otherwise it reads as comparable with the years around it.
                        const months = Number(point?.monthsObserved);
                        if (Number.isFinite(months) && months > 0 && months < 12) {
                            notes.push(`Average of ${months} of 12 months`);
                        }

                        if (point?.flagDescription) notes.push(point.flagDescription);

                        return notes.join(" - ");
                    },
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                footerFont: { weight: "normal" },
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: false,
                text: title,
                font: {
                    size: 14,
                    lineHeight: 1.2,
                },
                padding: { top: 0, left: 0, right: 0, bottom: 30 },
            },
            zoom: {
                limits: {
                    x: {
                        min: 'original',
                        max: 'original',
                        minRange: 2
                    },
                    y: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    }
                },
                zoom: {
                    wheel: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: false
                    },
                    drag: {
                        enabled: true,
                        backgroundColor: 'rgba(225,225,225,0.3)',
                        borderColor: 'rgb(225,225,225)',
                        borderWidth: 1,
                        threshold: 100,
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    rangeMin: {
                        x: null,
                        y: null
                    },
                    rangeMax: {
                        x: null,
                        y: null
                    }
                }
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Line ref={ref} options={options} data={chartData} />
            {/* Remounted when the scale type changes: the limits it captured on a linear axis
                start at zero, which a logarithmic axis cannot show. */}
            <ZoomControls key={useLogScale ? "log" : "linear"} chartRef={ref} data={chartData} />
        </Box>
    );
}
